import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { AuthProvider } from "../context/AuthContext";
import { ToastProvider } from "../components/Toaster";
import Navbar from "../components/Navbar";
import WebsiteShell from "../WebsiteShell";

// Import lightweight pages
import Home from "../pages";
import JoinQuiz from "../pages/JoinQuiz";
import JoinRoom from "../pages/JoinRoom";
import JoinQuizRoom from "../pages/JoinQuizRoom";
import Audience from "../pages/Audience";
import QuizPlay from "../pages/QuizPlay";
import Login from "../pages/login";
import Help from "../pages/Help";
import PowerPointHelp from "../pages/help/PowerPoint";
import RequireAuth from "../components/RequireAuth";
import RequireAdmin from "../components/RequireAdmin";

// Lazy load heavier components
const Host = React.lazy(() => import("../pages/Host"));
const EditQuiz = React.lazy(() => import("../pages/EditQuiz"));
const HostScreen = React.lazy(() => import("../pages/HostScreen"));
const NewQuizWizard = React.lazy(() => import("../components/NewQuizWizard"));

// Lazy load admin pages
const AdminHome = React.lazy(() => import("../pages/admin/AdminHome"));
const AdminLogs = React.lazy(() => import("../pages/admin/AdminLogs"));
const AdminUsers = React.lazy(() => import("../pages/admin/AdminUsers"));
const Telemetry = React.lazy(() => import("../pages/admin/Telemetry"));

// Office Add-in pages
const OfficeHome = React.lazy(() => import("../office/OfficeHome"));
const PresenterPanel = React.lazy(() => import("../office/screens/PresenterPanel").then(m => ({ default: m.PresenterPanel })));
const PresenterPolls = React.lazy(() => import("../office/pages/PresenterPolls"));
const PresenterQna = React.lazy(() => import("../office/pages/PresenterQna"));
const CreateQuiz = React.lazy(() => import("../office/pages/CreateQuiz"));
const Quizzes = React.lazy(() => import("../office/pages/Quizzes"));

function AppContent() {
  const location = useLocation();
  const isOfficePath = location.pathname.startsWith('/office');

  return (
    <WebsiteShell>
      {!isOfficePath && <Navbar />}
      <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/join/:code" element={<JoinQuiz />} />
                <Route path="/join" element={<JoinRoom />} />
                <Route path="/room/:id" element={<JoinQuizRoom />} />
                <Route path="/audience/:code" element={<Audience />} />
                <Route path="/play/:quizId" element={<QuizPlay />} />
                <Route path="/login" element={<Login />} />
                <Route path="/help" element={<Help />} />
                <Route path="/help/powerpoint" element={<PowerPointHelp />} />
                <Route 
                  path="/admin" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <RequireAdmin>
                          <AdminHome />
                        </RequireAdmin>
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/admin/logs" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <RequireAdmin>
                          <AdminLogs />
                        </RequireAdmin>
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <RequireAdmin>
                          <AdminUsers />
                        </RequireAdmin>
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/admin/telemetry" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <RequireAdmin>
                          <Telemetry />
                        </RequireAdmin>
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/host" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <Host />
                    </React.Suspense>
                  } 
                />
                {/* Add route for specific quiz in host view */}
                <Route 
                  path="/host/:id" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <Host />
                    </React.Suspense>
                  } 
                />
                <Route
                  path="/quiz/edit/:quizId"
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <EditQuiz />
                      </RequireAuth>
                    </React.Suspense>
                  }
                />
                <Route
                  path="/host-quiz/:quizId"
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <HostScreen />
                      </RequireAuth>
                    </React.Suspense>
                  }
                />
                {/* Office Add-in Routes */}
                <Route 
                  path="/office" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <OfficeHome />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/office/present/:quizId" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <PresenterPanel />
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/office/presenter" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <PresenterPolls />
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/office/qna" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <PresenterQna />
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/office/create" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <CreateQuiz />
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/office/wizard" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <NewQuizWizard />
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="/office/quizzes" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <RequireAuth>
                        <Quizzes />
                      </RequireAuth>
                    </React.Suspense>
                  } 
                />
                {/* Catch-all route for 404 */}
                <Route 
                  path="*" 
                  element={
                    <div className="flex flex-col items-center justify-center min-h-screen">
                      <h1 className="text-4xl font-bold mb-4">404 - Page Not Found</h1>
                      <p className="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
                      <a href="/" className="text-blue-600 hover:underline">Return to Home</a>
                    </div>
                  } 
                />
              </Routes>
            </WebsiteShell>
  );
}

export default function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

